import * as THREE from "three"
import {
  createRoomShellGridTextureCanvas,
  createWalkableShapeGeometry,
  getRoomShellWallTint,
  getRoomShellWallDescriptors,
  projectRoomShellGuide,
  ROOM_SHELL_CAMERA_CONFIG,
  ROOM_SHELL_DEBUG_FLOOR_COLOR,
  ROOM_SHELL_DEBUG_FLOOR_OPACITY,
  ROOM_SHELL_DEBUG_GRID_STROKE,
  ROOM_SHELL_DEBUG_WALL_FILL_OPACITY,
  ROOM_SHELL_DEBUG_WALL_GRID_OPACITY,
  type WalkableShape,
} from "./room-shell-geometry"

const STAGE_WIDTH = 640
const STAGE_HEIGHT = 360

export type RoomShellReferenceRender = {
  kind: "room-shell-v1"
  mimeType: "image/png"
  imageB64: string
}

function encodeDataUrlToBase64(dataUrl: string) {
  return dataUrl.replace(/^data:image\/png;base64,/, "")
}

function createGridWall({
  width,
  height,
  repeatX,
  repeatY,
  position,
  rotation,
  gridStrokeStyle = ROOM_SHELL_DEBUG_GRID_STROKE,
  color,
}: {
  width: number
  height: number
  repeatX: number
  repeatY: number
  position: THREE.Vector3
  rotation: THREE.Euler
  gridStrokeStyle?: string
  color: string
}) {
  const texture = new THREE.CanvasTexture(
    createRoomShellGridTextureCanvas(gridStrokeStyle)
  )
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  texture.repeat.set(repeatX, repeatY)
  texture.colorSpace = THREE.SRGBColorSpace

  const material = new THREE.MeshBasicMaterial({
    map: texture,
    color,
    transparent: true,
    side: THREE.DoubleSide,
    opacity: ROOM_SHELL_DEBUG_WALL_GRID_OPACITY,
  })

  const wall = new THREE.Mesh(new THREE.PlaneGeometry(width, height), material)
  wall.position.copy(position)
  wall.rotation.copy(rotation)
  return wall
}

function createThreeRoomShellReference(walkableShape: WalkableShape) {
  const canvas = document.createElement("canvas")
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: false,
    preserveDrawingBuffer: true,
  })
  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.setPixelRatio(1)
  renderer.setSize(STAGE_WIDTH, STAGE_HEIGHT, false)
  renderer.setClearColor(0xffffff, 1)

  const scene = new THREE.Scene()
  scene.background = new THREE.Color(0xffffff)

  const camera = new THREE.PerspectiveCamera(
    ROOM_SHELL_CAMERA_CONFIG.fov,
    STAGE_WIDTH / STAGE_HEIGHT,
    0.1,
    100
  )
  camera.position.set(
    ROOM_SHELL_CAMERA_CONFIG.position.x,
    ROOM_SHELL_CAMERA_CONFIG.position.y,
    ROOM_SHELL_CAMERA_CONFIG.position.z
  )
  camera.lookAt(
    ROOM_SHELL_CAMERA_CONFIG.lookAt.x,
    ROOM_SHELL_CAMERA_CONFIG.lookAt.y,
    ROOM_SHELL_CAMERA_CONFIG.lookAt.z
  )

  const ambient = new THREE.AmbientLight(0xffffff, 1)
  scene.add(ambient)

  const floor = new THREE.Mesh(
    createWalkableShapeGeometry(walkableShape),
    new THREE.MeshBasicMaterial({
      color: ROOM_SHELL_DEBUG_FLOOR_COLOR,
      transparent: true,
      opacity: ROOM_SHELL_DEBUG_FLOOR_OPACITY,
      side: THREE.DoubleSide,
    })
  )
  floor.rotation.x = -Math.PI / 2
  floor.position.y = 0.015
  scene.add(floor)

  const wallMeshes = getRoomShellWallDescriptors(walkableShape).flatMap((wall) => {
    const position = new THREE.Vector3(
      wall.position.x,
      wall.position.y,
      wall.position.z
    )
    const rotation = new THREE.Euler(
      wall.rotation.x,
      wall.rotation.y,
      wall.rotation.z
    )
    const wallTint = getRoomShellWallTint(wall.key)

    const fill = new THREE.Mesh(
      new THREE.PlaneGeometry(wall.width, wall.height),
      new THREE.MeshBasicMaterial({
        color: wallTint,
        transparent: true,
        opacity: ROOM_SHELL_DEBUG_WALL_FILL_OPACITY,
        side: THREE.DoubleSide,
      })
    )
    fill.position.copy(position)
    fill.rotation.copy(rotation)

    const grid = createGridWall({
      width: wall.width,
      height: wall.height,
      repeatX: wall.repeatX,
      repeatY: wall.repeatY,
      position,
      rotation,
      color: wallTint,
    })

    return [fill, grid]
  })

  scene.add(...wallMeshes)
  renderer.render(scene, camera)
  const dataUrl = renderer.domElement.toDataURL("image/png")

  floor.geometry.dispose()
  ;(floor.material as THREE.Material).dispose()
  for (const wall of wallMeshes) {
    wall.geometry.dispose()
    const material = wall.material as THREE.MeshBasicMaterial
    material.map?.dispose()
    material.dispose()
  }
  renderer.dispose()

  return dataUrl
}

function createCanvasFallbackReference(walkableShape: WalkableShape) {
  const canvas = document.createElement("canvas")
  canvas.width = STAGE_WIDTH
  canvas.height = STAGE_HEIGHT
  const context = canvas.getContext("2d")

  if (!context) {
    throw new Error("Unable to create room-shell reference canvas")
  }

  context.fillStyle = "#ffffff"
  context.fillRect(0, 0, canvas.width, canvas.height)

  const layout = projectRoomShellGuide(
    walkableShape,
    STAGE_WIDTH,
    STAGE_HEIGHT
  )

  context.fillStyle = ROOM_SHELL_DEBUG_FLOOR_COLOR
  context.globalAlpha = ROOM_SHELL_DEBUG_FLOOR_OPACITY
  context.beginPath()
  context.moveTo(layout.floor[0].x, layout.floor[0].y)
  for (const point of layout.floor.slice(1)) {
    context.lineTo(point.x, point.y)
  }
  context.closePath()
  context.fill()
  context.globalAlpha = 1

  const wallEntries = Object.entries(layout.walls) as Array<
    [keyof typeof layout.walls, (typeof layout.walls)[keyof typeof layout.walls]]
  >

  for (const [wallKey, wall] of wallEntries) {
    const wallTint = getRoomShellWallTint(wallKey)
    context.fillStyle = wallTint
    context.globalAlpha = ROOM_SHELL_DEBUG_WALL_FILL_OPACITY
    context.beginPath()
    context.moveTo(wall.polygon[0].x, wall.polygon[0].y)
    for (const point of wall.polygon.slice(1)) {
      context.lineTo(point.x, point.y)
    }
    context.closePath()
    context.fill()
  }

  context.globalAlpha = ROOM_SHELL_DEBUG_WALL_GRID_OPACITY
  context.strokeStyle = ROOM_SHELL_DEBUG_GRID_STROKE
  context.lineWidth = 1.2
  for (const [wallKey, wall] of wallEntries) {
    context.save()
    context.strokeStyle = getRoomShellWallTint(wallKey)
    for (const line of wall.gridLines) {
      context.beginPath()
      context.moveTo(line.start.x, line.start.y)
      context.lineTo(line.end.x, line.end.y)
      context.stroke()
    }
    context.restore()
  }
  context.globalAlpha = 1

  return canvas.toDataURL("image/png")
}

export async function createRoomShellReferenceRender(
  walkableShape: WalkableShape = "square"
): Promise<RoomShellReferenceRender> {
  const dataUrl = (() => {
    const shouldPreferCanvasFallback =
      typeof navigator !== "undefined" && navigator.webdriver

    try {
      if (shouldPreferCanvasFallback) {
        return createCanvasFallbackReference(walkableShape)
      }
      return createThreeRoomShellReference(walkableShape)
    } catch {
      return createCanvasFallbackReference(walkableShape)
    }
  })()

  return {
    kind: "room-shell-v1",
    mimeType: "image/png",
    imageB64: encodeDataUrlToBase64(dataUrl),
  }
}
